import { fromUnixTime, getYear } from 'date-fns';

// 元号の定義
export interface Era {
  name: string;
  nameShort: string;
  startYear: number;
}

// 日本の元号一覧
export const japaneseEras: Era[] = [
  { name: '令和', nameShort: 'R', startYear: 2019 },
  { name: '平成', nameShort: 'H', startYear: 1989 },
  { name: '昭和', nameShort: 'S', startYear: 1926 },
  { name: '大正', nameShort: 'T', startYear: 1912 },
  { name: '明治', nameShort: 'M', startYear: 1868 },
];

/**
 * 西暦から和暦に変換する
 * @param westernYear 西暦年
 * @returns 和暦情報（元号と和暦年）またはnull
 */
export function convertToJapaneseEra(westernYear: number): { era: Era; year: number } | null {
  if (!westernYear || westernYear <= 0 || isNaN(westernYear)) return null;

  try {
    // Intl.DateTimeFormatを使用して和暦に変換
    const date = new Date(westernYear, 0, 1); // その年の1月1日
    const formatter = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
      era: 'long',
      year: 'numeric'
    });
    
    const parts = formatter.formatToParts(date);
    const eraName = parts.find(part => part.type === 'era')?.value || '';
    const yearString = parts.find(part => part.type === 'year')?.value || '';
    const year = parseInt(yearString, 10);
    
    // 元号名を標準化（「令和」のような形式に）
    let normalizedEraName = eraName.replace(/元年$/, '');
    
    // 対応する元号を検索
    const era = japaneseEras.find(e => normalizedEraName.includes(e.name));
    
    if (era && !isNaN(year)) {
      return { era, year };
    }
    
    // Intl.DateTimeFormatで変換できない場合は従来のロジックを使用
    for (const era of japaneseEras) {
      if (westernYear >= era.startYear) {
        return {
          era,
          year: westernYear - era.startYear + 1
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('和暦変換エラー:', error);
    
    // エラー時は従来のロジックを使用
    for (const era of japaneseEras) {
      if (westernYear >= era.startYear) {
        return {
          era,
          year: westernYear - era.startYear + 1
        };
      }
    }
    
    return null;
  }
}

/**
 * 和暦から西暦に変換する
 * @param eraName 元号名
 * @param year 和暦年
 * @returns 西暦年またはnull
 */
export function convertToWesternYear(eraName: string, year: number): number | null {
  if (!eraName || eraName.length === 0 || !year || year <= 0 || isNaN(year)) return null;
  
  const era = japaneseEras.find(e => e.name === eraName);
  if (!era) return null;
  
  return era.startYear + year - 1;
}

/**
 * Unix時間から和暦情報を取得する
 * @param unixTime Unix時間
 * @returns 和暦情報（元号と和暦年）またはnull
 */
export function getJapaneseEraFromUnixTime(unixTime: number): { era: Era; year: number } | null {
  if (!unixTime || unixTime === 0 || isNaN(unixTime)) return null;
  
  try {
    const date = fromUnixTime(unixTime);
    const westernYear = getYear(date);
    return convertToJapaneseEra(westernYear);
  } catch (error) {
    console.error('Unix時間からの和暦変換エラー:', error);
    return null;
  }
}

/**
 * 和暦表示用の文字列を生成する
 * @param westernYear 西暦年
 * @returns 和暦表示文字列（例: "令和3年"）
 */
export function formatJapaneseEraYear(westernYear: number): string {
  if (!westernYear || westernYear <= 0 || isNaN(westernYear)) return '';
  
  try {
    // Intl.DateTimeFormatを使用して和暦表示を生成
    const date = new Date(westernYear, 0, 1);
    const formatter = new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
      era: 'long',
      year: 'numeric'
    });
    
    return formatter.format(date);
  } catch (error) {
    console.error('和暦フォーマットエラー:', error);
    
    // エラー時は従来のロジックを使用
    const japaneseEra = convertToJapaneseEra(westernYear);
    if (japaneseEra === null) return `${westernYear}年`;
    
    return `${japaneseEra.era.name}${japaneseEra.year}年`;
  }
} 